import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { motion, AnimatePresence } from 'framer-motion'

import { primary } from '../theme/palette'

/// Floating "N selected · Delete" pill that slides in from the
/// viewport bottom when the user has any rows checked. Fixed-positioned
/// so it always lands in the screen's horizontal center regardless of
/// which container renders it — narrow master-detail list panes were
/// previously squeezing the bar's text. Disappears entirely when
/// nothing is selected.
export function BulkActionBar({
  count, onClear, onDelete, deleting, label,
}: {
  count: number
  onClear: () => void
  onDelete: () => void
  deleting?: boolean
  /** Singular label e.g. "vendor" — used in "3 vendors selected". */
  label: string
}) {
  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.18 }}
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 28,
            zIndex: 1300,
            // Stop the pill from trying to shrink to its container —
            // it sizes to content instead.
            width: 'max-content',
          }}
        >
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.75, py: 1, borderRadius: 999,
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
            boxShadow: '0 12px 36px rgba(15, 23, 42, 0.22)',
            whiteSpace: 'nowrap',
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton size="small" onClick={onClear} disabled={deleting}
                sx={{ width: 28, height: 28 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {count} {label}{count === 1 ? '' : 's'} selected
              </Typography>
            </Stack>
            <Button
              size="small" variant="contained" color="error"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={onDelete} disabled={deleting}
              sx={{
                fontSize: 12.5, fontWeight: 700, borderRadius: 999, px: 1.75,
                boxShadow: primary.glow, whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {deleting ? 'Deleting…' : 'Delete selected'}
            </Button>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
